import { icon } from "../icons.js";
import { esc, richText } from "./html.js";
import { fmtDateRange, nightsWord } from "../lib/dates.js";
import { fmtRub } from "../lib/money.js";
import { prepaymentLabel } from "../lib/avail.js";
import { restAtCheckin, agreeHtml } from "../lib/checkout.js";
import { carousel, SHOT_SIZES } from "./cards.js";
import { servicesBlock } from "./services.js";
import { guestsSummary } from "./searchbar.js";

// Текст согласия, если Шелтер не отдал свой (agreeText в getHotelParams)
const DEFAULT_AGREE_TEXT =
  "Я согласен с правилами онлайн-бронирования и обработкой персональных данных.";

// Суммы карточки-итога: на форме — цена карточки (выбор услуг и 409 price_changed index.js
// патчит в DOM без редрава), после брони — ответ сервера, где уже есть услуги и актуальная
// цена (T32b): в card услуг нет, а точечные правки формы редрав done-экрана стирает.
function summarySums({ card, phase, result }) {
  if (phase === "done" && result) {
    const services = result.servicesTotal ?? 0;
    return {
      total: result.total,
      services,
      grand: result.grandTotal ?? result.total + services,
      prepay: result.prepaymentRub ?? null,
      rest: result.restRub ?? null,
    };
  }
  return {
    total: card.total,
    services: 0,
    grand: card.total,
    prepay: card.prepaymentRub ?? null,
    rest: restAtCheckin(card.total, card.prepaymentRub),
  };
}

// Карточка-итог слева (ref_6): фото, описание, параметры поездки, СТОИМОСТЬ и ОПЛАТА.
// Суммы помечены data-okb-sum — при 409 price_changed index.js обновляет их точечно.
function summaryCard(state) {
  const { card, search } = state.checkout;
  const w = state.config?.widget ?? {};
  const m = card.name.match(/^(.*?)\s*\((.+)\)\s*$/);
  const title = esc(m ? m[1] : card.name);
  const sub = m ? `<small>${esc(m[2])}</small>` : "";
  const sums = summarySums(state.checkout);
  const pct = prepaymentLabel(sums.total, sums.prepay);
  const times =
    w.checkinFrom || w.checkoutUntil
      ? `<div class="okb-corow">${icon("clock")}<span>${
          w.checkinFrom ? `Заезд с ${esc(w.checkinFrom)}` : ""
        }${w.checkinFrom && w.checkoutUntil ? " • " : ""}${
          w.checkoutUntil ? `Выезд до ${esc(w.checkoutUntil)}` : ""
        }</span></div>`
      : "";
  const tariff = card.tariff
    ? `<div class="okb-corow">${icon("tag")}<span>Тариф: <b>${esc(card.tariff.name)}</b></span></div>
       ${
         card.tariff.description
           ? `<details class="okb-acc okb-coacc"><summary>Условия тарифа ${icon("chevronDown")}</summary><div class="okb-accbody">${richText(card.tariff.description)}</div></details>`
           : ""
       }`
    : "";
  return `
  <div class="okb-cosummary okb-card">
    ${carousel(card, SHOT_SIZES.summary)}
    <div class="okb-cobody">
      <div class="okb-cardhead"><span class="okb-name">${title}${sub}</span></div>
      ${
        card.description
          ? `<details class="okb-acc okb-coacc"><summary>Описание номера ${icon("chevronDown")}</summary><div class="okb-accbody">${richText(card.description)}</div></details>`
          : ""
      }
      <div class="okb-corows">
        <div class="okb-corow">${icon("calendar")}<span>${fmtDateRange(search.checkin, search.checkout)} · ${search.L} ${nightsWord(search.L)}</span></div>
        ${times}
        <div class="okb-corow">${icon("guest")}<span>${guestsSummary(search)}</span></div>
        ${tariff}
      </div>
      <div class="okb-cosec">Стоимость</div>
      <div class="okb-coline"><span>Проживание</span><b data-okb-sum="total">${fmtRub(sums.total)}</b></div>
      <div class="okb-coline" data-okb-svcline${sums.services > 0 ? "" : " hidden"}><span>Дополнительные услуги</span><b data-okb-sum="services">${fmtRub(sums.services)}</b></div>
      <div class="okb-coline okb-cototal"><span>Итого</span><b data-okb-sum="grand">${fmtRub(sums.grand)}</b></div>
      ${
        sums.prepay != null
          ? `<div class="okb-cosec">Оплата</div>
      <div class="okb-coline okb-cototal"><span>К оплате сейчас${pct ? ` <span class="okb-prepaypct">${pct}</span>` : ""}</span><b data-okb-sum="prepay">${fmtRub(sums.prepay)}</b></div>
      <div class="okb-coline"><span>Остаток при заселении</span><b data-okb-sum="rest">${sums.rest != null ? fmtRub(sums.rest) : "—"}</b></div>`
          : ""
      }
    </div>
  </div>`;
}

function field(name, label, { required = true, type = "text", placeholder = "", hint = "" } = {}) {
  return `
  <div class="okb-field" data-okb-field="${name}">
    <label for="okb-f-${name}">${label}${required ? " *" : ""}</label>
    <input class="okb-input" id="okb-f-${name}" name="${name}" type="${type}"
      ${placeholder ? `placeholder="${esc(placeholder)}"` : ""} autocomplete="${
        {
          firstName: "given-name",
          middleName: "additional-name",
          lastName: "family-name",
          phone: "tel",
          email: "email",
        }[name] ?? "on"
      }">
    ${hint ? `<small class="okb-fieldhint">${hint}</small>` : ""}
    <small class="okb-err" hidden></small>
  </div>`;
}

// Способ оплаты: радио из /api/payment-options; единственная опция предвыбрана
function paymentBlock(checkout) {
  if (checkout.optionsError) {
    return `
    <div class="okb-cocard">
      <h3>Способ оплаты</h3>
      <div class="okb-conotice">${esc(checkout.optionsError)}</div>
      <button type="button" class="okb-colink" data-okb="co-reload-options">Попробовать ещё раз</button>
    </div>`;
  }
  const opts = checkout.options ?? [];
  // Единственный способ («Оплата в отеле», T9.5g): блок не показываем — оплату ведёт менеджер
  // отдела бронирования; id всё равно обязателен в putOrder и уходит скрытым полем
  if (opts.length === 1) return `<input type="hidden" name="payment" value="${opts[0].id}">`;
  const items = opts
    .map(
      (o, i) => `
    <label class="okb-payopt">
      <input type="radio" name="payment" value="${o.id}" ${i === 0 ? "checked" : ""}>
      <span class="okb-payoptbody">
        ${o.image ? `<img src="${esc(o.image)}" alt="" loading="lazy">` : ""}
        <span><b>${esc(o.name)}</b>${o.description ? `<small>${esc(o.description)}</small>` : ""}</span>
      </span>
    </label>`,
    )
    .join("");
  return `
  <div class="okb-cocard">
    <h3>Способ оплаты</h3>
    ${items || `<div class="okb-conotice">Способы оплаты не настроены — позвоните нам, поможем с бронью.</div>`}
  </div>`;
}

function guestForm(state) {
  const checkout = state.checkout;
  const bf = state.config?.bookingForm ?? {};
  const bookingEnabled = state.config?.bookingEnabled === true;
  // ссылки на правила и обработку ПДн внутри текста согласия (T9.5h)
  const agree = agreeHtml(
    bf.agreeText ?? DEFAULT_AGREE_TEXT,
    { agreeUrl: bf.agreeUrl, pdUrl: bf.pdUrl },
    esc,
  );
  return `
  <form data-okb-form="checkout" novalidate>
    <div class="okb-cocard">
      <h3>Контактные данные</h3>
      <p class="okb-cohint">Отправим подтверждение бронирования и свяжемся при необходимости.</p>
      <div class="okb-cogrid okb-cogrid3">
        ${field("lastName", "Фамилия")}
        ${field("firstName", "Имя")}
        ${field("middleName", "Отчество", { required: false })}
      </div>
      <div class="okb-cogrid">
        ${field("phone", "Телефон", { type: "tel", placeholder: "+7 (999) 123-45-67" })}
        ${field("email", "Email", { type: "email" })}
      </div>
      <div class="okb-extra" aria-hidden="true"><label>Не заполняйте это поле<input type="text" name="okb_extra" tabindex="-1" autocomplete="off"></label></div>
    </div>
    ${servicesBlock(checkout, checkout.search.L)}
    <div class="okb-cocard">
      <h3>Комментарий для отеля</h3>
      <div class="okb-field" data-okb-field="comment">
        <textarea class="okb-input okb-textarea" name="comment" rows="3"
          placeholder="Пожелания, особые просьбы, время заезда…"></textarea>
        <small class="okb-fieldhint">Необязательно, до 1000 символов</small>
        <small class="okb-err" hidden></small>
      </div>
    </div>
    ${paymentBlock(checkout)}
    <div class="okb-field okb-agree" data-okb-field="agree">
      <label><input type="checkbox" name="agree"><span>${agree}</span></label>
      <small class="okb-err" hidden></small>
    </div>
    ${
      bookingEnabled
        ? ""
        : `<div class="okb-conotice">Онлайн-бронирование временно недоступно. Позвоните или напишите нам, чтобы забронировать.</div>`
    }
    <div class="okb-coerror" hidden></div>
    <button type="submit" class="okb-submitbtn" disabled>Забронировать</button>
    <p class="okb-cohint okb-cowait" aria-live="polite" hidden></p>
  </form>`;
}

// Экран результата (T9.5g): оплату проводит менеджер отдела бронирования («Оплата в отеле» в
// Шелтере — единственный способ) — плашка про менеджера показывается всегда, в том числе в демо.
// Кнопка «Перейти к оплате» — только при настоящей ссылке онлайн-оплаты из Шелтера; демо-ссылка
// на /pay нашего сервера гостю не показывается (просьба владельца 10.09.2026).
// Дизайна на референсах нет — выдержан в стилистике карточек.
function doneScreen(state) {
  const r = state.checkout.result;
  const bf = state.config?.bookingForm ?? {};
  const target = bf.openPaymentInNewTab ? ` target="_blank" rel="noopener"` : "";
  const payLink = r.linkToPay && !r.demo ? r.linkToPay : null;
  const share = prepaymentLabel(r.total, r.prepaymentRub);
  const manager = `<div class="okb-conotice okb-comanager">Менеджер отдела бронирования свяжется с вами
    для оплаты${share ? ` (предоплата — ${share} стоимости)` : ""}.</div>`;
  // доп. услуги (T11): подзаголовок и строки услуг, отдельной строки с их суммой нет — заказчик
  // счёл её дублем (T32a); «Итого» — с услугами, остаток сервер уже посчитал с услугами
  const services = Array.isArray(r.services) ? r.services : [];
  const svcRows = services.length
    ? `<div class="okb-coline okb-cosub"><span>Дополнительные услуги:</span></div>
    ${services
      .map(
        (s) =>
          `<div class="okb-coline okb-cosvc"><span>${esc(s.name)}${s.count > 1 ? ` × ${s.count}` : ""}</span><b>${fmtRub(s.price * s.count)}</b></div>`,
      )
      .join("")}
    <div class="okb-coline okb-cototal"><span>Итого</span><b>${fmtRub(r.grandTotal ?? r.total)}</b></div>`
    : "";
  // услуги оплачиваются по факту при заселении (T32c) — приписка только когда они в заказе
  const svcNote = services.length
    ? `<p class="okb-cohint okb-cosvcnote">Дополнительные услуги оплачиваются по факту при заселении и в предоплату не входят.</p>`
    : "";
  const sums = `
    <div class="okb-coline"><span>Стоимость проживания</span><b>${fmtRub(r.total)}</b></div>
    ${svcRows}
    ${
      r.prepaymentRub != null
        ? `<div class="okb-coline"><span>К оплате сейчас</span><b>${fmtRub(r.prepaymentRub)}</b></div>
           <div class="okb-coline"><span>Остаток при заселении</span><b>${r.restRub != null ? fmtRub(r.restRub) : "—"}</b></div>`
        : ""
    }
    ${svcNote}`;
  return `
  <div class="okb-cocard okb-codone">
    <h3>Бронь №${esc(String(r.bookingCode))} создана</h3>
    ${
      payLink
        ? `<p class="okb-cohint">Осталось внести предоплату — после оплаты бронь подтверждается автоматически.
           Подтверждение придёт на указанный e-mail.</p>
           ${sums}
           <a class="okb-submitbtn okb-paylink" href="${esc(payLink)}"${target}>Перейти к оплате</a>`
        : `${manager}
           <p class="okb-cohint">Подтверждение придёт на указанный e-mail.</p>
           ${sums}`
    }
    <button type="button" class="okb-colink" data-okb="checkout-back">← Вернуться к поиску</button>
  </div>`;
}

// Экран оформления (ref_6/ref_7): крошка, заголовок, слева итог, справа форма
export function renderCheckout(state) {
  const checkout = state.checkout;
  const head = `
  <button type="button" class="okb-crumb" data-okb="checkout-back">← Вернуться к выбору номера</button>
  <div class="okb-cohead">
    <div>
      <h2>Оформление бронирования</h2>
      <p class="okb-cohint">Проверьте детали и укажите контактные данные</p>
    </div>
  </div>`;
  if (checkout.phase === "loading") {
    return `${head}<div class="okb-hintcard">${icon("clock")}<div><h3>Готовим оформление…</h3><p>Загружаем способы оплаты и услуги.</p></div></div>`;
  }
  if (checkout.phase === "done") {
    return `${head.replace(/<button[^>]*okb-crumb[\s\S]*?<\/button>/, "")}
    <div class="okb-checkout"><div>${summaryCard(state)}</div><div>${doneScreen(state)}</div></div>`;
  }
  return `${head}<div class="okb-checkout"><div>${summaryCard(state)}</div><div>${guestForm(state)}</div></div>`;
}
